import { createApp } from 'vue'
import FeedbackMessageBox from '@/components/feedback/FeedbackMessageBox.vue'

interface MessageBoxOptions {
  title?: string
  message?: string
  showClose?: boolean
  showFooter?: boolean
  showCancelButton?: boolean
  showConfirmButton?: boolean
  cancelButtonText?: string
  confirmButtonText?: string
  cancelButtonType?: string
  confirmButtonType?: string
  type?: 'success' | 'error' | 'warning' | 'info'
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void | Promise<void>
  onClose?: () => void | Promise<void>
}

class MessageBoxManager {
  private currentApp: any = null
  private currentDiv: HTMLElement | null = null

  show(options: MessageBoxOptions) {
    // 销毁之前的对话框
    this.destroy()

    // 创建新的容器元素
    const div = document.createElement('div')
    document.body.appendChild(div)
    this.currentDiv = div

    const defaultOptions: MessageBoxOptions = {
      title: '',
      showClose: true,
      showFooter: true,
      showCancelButton: true,
      showConfirmButton: true,
      cancelButtonText: '取消',
      confirmButtonText: '确定',
      cancelButtonType: 'info',
      confirmButtonType: 'default',
      onConfirm: () => {},
      onCancel: () => {},
      onClose: () => {}
    }

    const mergedOptions = { ...defaultOptions, ...options }

    // 创建Vue应用并挂载组件
    const app = createApp(FeedbackMessageBox, {
      visible: true,
      title: mergedOptions.title,
      message: mergedOptions.message,
      showClose: mergedOptions.showClose,
      showFooter: mergedOptions.showFooter,
      showCancelButton: mergedOptions.showCancelButton,
      showConfirmButton: mergedOptions.showConfirmButton,
      cancelButtonText: mergedOptions.cancelButtonText,
      confirmButtonText: mergedOptions.confirmButtonText,
      cancelButtonType: mergedOptions.cancelButtonType,
      confirmButtonType: mergedOptions.confirmButtonType,
      type: mergedOptions.type,
      'onUpdate:visible': (visible: boolean) => {
        if (!visible) {
          this.destroy()
        }
      },
      onConfirm: () => {
        if (typeof mergedOptions.onConfirm === 'function') {
          mergedOptions.onConfirm()
        }
      },
      onCancel: () => {
        if (typeof mergedOptions.onCancel === 'function') {
          mergedOptions.onCancel()
        }
        this.destroy()
      },
      onClose: () => {
        if (typeof mergedOptions.onClose === 'function') {
          mergedOptions.onClose()
        }
        this.destroy()
      }
    })

    this.currentApp = app
    app.mount(div)
  }

  // 销毁当前对话框
  private destroy() {
    if (this.currentApp && this.currentDiv) {
      this.currentApp.unmount()
      if (this.currentDiv.parentNode) {
        this.currentDiv.parentNode.removeChild(this.currentDiv)
      }
      this.currentApp = null
      this.currentDiv = null
    }
  }

  // 显示确认对话框，返回Promise<boolean>
  confirm(message: string, config?: MessageBoxOptions): Promise<boolean> {
    const defaultConfig: MessageBoxOptions = {
      message,
      showClose: true,
      showFooter: true,
      showCancelButton: true,
      showConfirmButton: true,
      cancelButtonText: '取消',
      confirmButtonText: '确定',
      cancelButtonType: 'info',
      confirmButtonType: 'default',
      type: 'info'
    }

    return new Promise((resolve) => {
      const mergedConfig = {
        ...defaultConfig,
        ...config,
        onConfirm: async () => {
          try {
            if (typeof config?.onConfirm === 'function') {
              await config.onConfirm()
            }
            resolve(true)
          } catch (error) {
            console.error('Confirm handler error:', error)
            resolve(false)
          } finally {
            this.destroy()
          }
        },
        onCancel: async () => {
          try {
            if (typeof config?.onCancel === 'function') {
              await config.onCancel()
            }
          } catch (error) {
            console.error('Cancel handler error:', error)
          } finally {
            resolve(false)
          }
        },
        onClose: async () => {
          try {
            if (typeof config?.onClose === 'function') {
              await config.onClose()
            }
          } catch (error) {
            console.error('Close handler error:', error)
          } finally {
            resolve(false)
          }
        }
      }

      this.show(mergedConfig)
    })
  }

  // 显示删除确认对话框，返回Promise<boolean>
  confirmDel(message: string = '确认删除？', onConfirm?: () => void | Promise<void>): Promise<boolean> {
    const deleteConfig: MessageBoxOptions = {
      title: '删除告警',
      showClose: true,
      showFooter: true,
      showCancelButton: true,
      showConfirmButton: true,
      cancelButtonText: '取消',
      confirmButtonText: '删除',
      cancelButtonType: 'info',
      confirmButtonType: 'danger',
      type: 'warning',
      onConfirm
    }

    return this.confirm(message, deleteConfig)
  }
  warning(message: string = '告警内容', title: string = '告警') {
    const dfc: MessageBoxOptions = {
      title,
      showClose: true,
      showFooter: true,
      showCancelButton: false,
      showConfirmButton: true,
      cancelButtonText: '取消',
      confirmButtonText: '确定',
      cancelButtonType: 'info',
      confirmButtonType: 'warning',
      type: 'warning'
    }

    return this.confirm(message, dfc)
  }
  error(message: string = '错误内容', title: string = '错误', config?: MessageBoxOptions) {
    const dfc: MessageBoxOptions = {
      title,
      showClose: true,
      showFooter: true,
      showCancelButton: false,
      showConfirmButton: true,
      cancelButtonText: '取消',
      confirmButtonText: '确定',
      cancelButtonType: 'info',
      confirmButtonType: 'danger',
      type: 'error'
    }

    return this.confirm(message, { ...dfc, ...config })
  }
}

// 创建单例实例
const messageBoxManager = new MessageBoxManager()

// 导出useMessageBox函数，返回单例实例
export const useMessageBox = () => messageBoxManager

// 导出类型
export type MessageBoxManagerType = MessageBoxManager
